import type { StoreApi, UseBoundStore } from 'zustand';
import type { FeedbackContext, FeedbackIntent } from '@/config/feedback';
import { createTrackedStore } from '@/lib/dev/zustand-registry';

interface LayoutState {
  /** Shared portal host connects route-owned listing controls to the app header. */
  categoryActionsElement: HTMLDivElement | null;
  setCategoryActionsElement: (element: HTMLDivElement | null) => void;
  sidebarOpen: boolean;
  searchOpen: boolean;
  searchSession: number;
  /** Product lane selected when the global search modal next opens. */
  searchInitialTab: SearchInitialTab;
  mobileMenuOpen: boolean;
  /** When true, open the connect/auth gate modal (used by header and mobile menu). */
  connectAuthOpen: boolean;
  /** When true, open the manage wallets modal (wallet list, create, import, unlink). */
  manageWalletsOpen: boolean;
  /** When true, open the devnet faucet modal (claim test cash). Devnet-only. */
  faucetModalOpen: boolean;
  /** When true, open the reusable Feedback & Reports modal. */
  feedbackModalOpen: boolean;
  /** Intent the feedback modal opens with (presets type + copy). */
  feedbackIntent: FeedbackIntent;
  /** Optional context attached to the feedback submission (route, error digest, …). */
  feedbackContext: FeedbackContext | null;
  setSidebarOpen: (open: boolean) => void;
  setSearchOpen: (open: boolean) => void;
  openSearch: (initialTab?: SearchInitialTab) => void;
  setMobileMenuOpen: (open: boolean) => void;
  setConnectAuthOpen: (open: boolean) => void;
  setManageWalletsOpen: (open: boolean) => void;
  setFaucetModalOpen: (open: boolean) => void;
  /** Open the feedback modal with an optional intent + context. */
  openFeedback: (opts?: { intent?: FeedbackIntent; context?: FeedbackContext }) => void;
  setFeedbackModalOpen: (open: boolean) => void;
  toggleSidebar: () => void;
  toggleSearch: () => void;
}

export type SearchInitialTab = 'all' | 'live-events';

export const useLayoutStore: UseBoundStore<StoreApi<LayoutState>> = createTrackedStore<LayoutState>(
  'useLayoutStore',
)((set) => ({
  categoryActionsElement: null,
  setCategoryActionsElement: (element: HTMLDivElement | null): void => {
    set({ categoryActionsElement: element });
  },
  sidebarOpen: false,
  searchOpen: false,
  searchSession: 0,
  searchInitialTab: 'all',
  mobileMenuOpen: false,
  connectAuthOpen: false,
  manageWalletsOpen: false,
  faucetModalOpen: false,
  feedbackModalOpen: false,
  feedbackIntent: 'feedback',
  feedbackContext: null,
  setSidebarOpen: (open: boolean): void => {
    set({ sidebarOpen: open });
  },
  setSearchOpen: (open: boolean): void => {
    set((state) => {
      const searchSession =
        open && !state.searchOpen ? state.searchSession + 1 : state.searchSession;
      if (open) return { searchOpen: true, searchSession, searchInitialTab: 'all' };
      return { searchOpen: false, searchSession };
    });
  },
  openSearch: (initialTab: SearchInitialTab = 'all'): void => {
    set((state) => ({
      searchOpen: true,
      searchInitialTab: initialTab,
      searchSession: state.searchOpen ? state.searchSession : state.searchSession + 1,
    }));
  },
  setMobileMenuOpen: (open: boolean): void => {
    set({ mobileMenuOpen: open });
  },
  setConnectAuthOpen: (open: boolean): void => {
    set({ connectAuthOpen: open });
  },
  setManageWalletsOpen: (open: boolean): void => {
    set({ manageWalletsOpen: open });
  },
  setFaucetModalOpen: (open: boolean): void => {
    set({ faucetModalOpen: open });
  },
  openFeedback: (opts: { intent?: FeedbackIntent; context?: FeedbackContext } = {}): void => {
    set({
      feedbackModalOpen: true,
      feedbackIntent: opts.intent ?? 'feedback',
      feedbackContext: opts.context ?? null,
    });
  },
  setFeedbackModalOpen: (open: boolean): void => {
    set({ feedbackModalOpen: open });
  },
  toggleSidebar: (): void => {
    set((state) => ({ sidebarOpen: !state.sidebarOpen }));
  },
  toggleSearch: (): void => {
    set((state) => ({
      searchOpen: !state.searchOpen,
      searchSession: state.searchOpen ? state.searchSession : state.searchSession + 1,
    }));
  },
}));
